import React, { useState } from 'react';

interface StackedDataPoint {
  label: string;
  segments: {
    name: string;
    value: number;
    color: string;
  }[];
}

interface StackedBarChartProps {
  data: StackedDataPoint[];
  height?: number;
  showLegend?: boolean;
  showValues?: boolean;
  className?: string;
}

/**
 * Stacked Bar Chart Component
 * Shows multiple data series stacked on top of each other
 */
const StackedBarChart: React.FC<StackedBarChartProps> = ({
  data,
  height = 300,
  showLegend = true,
  showValues = true,
  className = '',
}) => {
  const [hoveredSegment, setHoveredSegment] = useState<{ barIndex: number; segmentIndex: number } | null>(null);

  if (!data || data.length === 0) {
    return (
      <div className={`flex items-center justify-center ${className}`} style={{ height }}>
        <div className="text-gray-400 text-sm">No data available</div>
      </div>
    );
  }

  // Calculate totals and max value
  const totals = data.map((point) =>
    point.segments.reduce((sum, seg) => sum + seg.value, 0)
  );
  const maxValue = Math.max(...totals);

  // Get all unique segment names for legend
  const allSegmentNames = new Set<string>();
  const segmentColors: { [key: string]: string } = {};
  data.forEach((point) => {
    point.segments.forEach((seg) => {
      allSegmentNames.add(seg.name);
      segmentColors[seg.name] = seg.color;
    });
  });
  const legendItems = Array.from(allSegmentNames);

  const barWidth = 100 / data.length - 4;
  const chartHeight = height - 80;

  return (
    <div className={`relative ${className}`}>
      {/* Chart */}
      <svg width="100%" height={height} className="overflow-visible">
        {/* Grid lines */}
        {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
          const y = height - 60 - ratio * chartHeight;
          const value = Math.round(maxValue * ratio);
          return (
            <g key={ratio}>
              <line
                x1="40"
                y1={y}
                x2="100%"
                y2={y}
                stroke="#e5e7eb"
                strokeWidth="1"
                strokeDasharray="4 4"
              />
              <text
                x="5"
                y={y}
                textAnchor="start"
                dominantBaseline="middle"
                className="text-xs fill-gray-600"
              >
                {value}
              </text>
            </g>
          );
        })}

        {/* Bars */}
        {data.map((point, barIndex) => {
          const x = (barIndex * 100) / data.length + 2;
          let cumulativeHeight = 0;
          const total = totals[barIndex];

          return (
            <g key={barIndex}>
              {/* Stacked segments */}
              {point.segments.map((segment, segmentIndex) => {
                const segmentHeight = (segment.value / maxValue) * chartHeight;
                const y = height - 60 - cumulativeHeight - segmentHeight;
                cumulativeHeight += segmentHeight;

                const isHovered =
                  hoveredSegment?.barIndex === barIndex &&
                  hoveredSegment?.segmentIndex === segmentIndex;

                return (
                  <g key={segmentIndex}>
                    <rect
                      x={`${x}%`}
                      y={y}
                      width={`${barWidth}%`}
                      height={segmentHeight}
                      fill={segment.color}
                      opacity={isHovered ? 1 : 0.85}
                      className="transition-all duration-200 cursor-pointer"
                      onMouseEnter={() =>
                        setHoveredSegment({ barIndex, segmentIndex })
                      }
                      onMouseLeave={() => setHoveredSegment(null)}
                      style={{
                        filter: isHovered ? 'brightness(1.1)' : 'none',
                      }}
                    />
                    {showValues && segmentHeight > 25 && (
                      <text
                        x={`${x + barWidth / 2}%`}
                        y={y + segmentHeight / 2}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        className="text-xs font-semibold fill-white pointer-events-none"
                        style={{
                          textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                        }}
                      >
                        {segment.value}
                      </text>
                    )}
                  </g>
                );
              })}

              {/* Total label above bar */}
              <text
                x={`${x + barWidth / 2}%`}
                y={height - 60 - cumulativeHeight - 8}
                textAnchor="middle"
                className="text-xs font-bold fill-gray-700"
              >
                {total}
              </text>

              {/* X-axis label */}
              <text
                x={`${x + barWidth / 2}%`}
                y={height - 20}
                textAnchor="middle"
                className="text-xs fill-gray-600"
              >
                {point.label.length > 10
                  ? `${point.label.substring(0, 10)}...`
                  : point.label}
              </text>
            </g>
          );
        })}

        {/* Tooltip for hovered segment */}
        {hoveredSegment !== null && (
          <g>
            {(() => {
              const point = data[hoveredSegment.barIndex];
              const segment = point.segments[hoveredSegment.segmentIndex];
              const x = (hoveredSegment.barIndex * 100) / data.length + 2 + barWidth / 2;
              
              let cumulativeHeight = 0;
              for (let i = 0; i <= hoveredSegment.segmentIndex; i++) {
                cumulativeHeight += (point.segments[i].value / maxValue) * chartHeight;
              }
              
              const y = height - 60 - cumulativeHeight + 
                        ((point.segments[hoveredSegment.segmentIndex].value / maxValue) * chartHeight) / 2;

              return (
                <>
                  <rect
                    x={`calc(${x}% + 10px)`}
                    y={y - 30}
                    width="140"
                    height="50"
                    fill="rgba(0, 0, 0, 0.9)"
                    rx="4"
                    className="pointer-events-none"
                  />
                  <text
                    x={`calc(${x}% + 80px)`}
                    y={y - 15}
                    textAnchor="middle"
                    className="text-xs font-semibold fill-white pointer-events-none"
                  >
                    {segment.name}
                  </text>
                  <text
                    x={`calc(${x}% + 80px)`}
                    y={y}
                    textAnchor="middle"
                    className="text-sm font-bold fill-white pointer-events-none"
                  >
                    {segment.value.toLocaleString()}
                  </text>
                  <text
                    x={`calc(${x}% + 80px)`}
                    y={y + 15}
                    textAnchor="middle"
                    className="text-xs fill-gray-300 pointer-events-none"
                  >
                    {point.label}
                  </text>
                </>
              );
            })()}
          </g>
        )}
      </svg>

      {/* Legend */}
      {showLegend && legendItems.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-4 justify-center">
          {legendItems.map((name, index) => (
            <div key={index} className="flex items-center gap-2">
              <div
                className="w-3 h-3 rounded-sm"
                style={{ backgroundColor: segmentColors[name] }}
              />
              <span className="text-sm text-gray-700">{name}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default StackedBarChart;
